/* The API client.
 *
 * Every figure rendered by this SPA is fetched, never computed here. That is
 * AI-03's rule extended to the whole front end: the calculation engine is the
 * single source of every number, so the grid cannot drift from the reports,
 * and NFR-07 traceability survives.
 */

export type Status = 'LOW' | 'OK' | 'HIGH'

export interface Bucket {
  index: number
  type: 'D' | 'W'
  week: number
  year: number
  label: string
  date: string
  plant_days: number
  customer_days: number
  max_days: number
  horizon_edge: boolean
}

export interface CrewRow {
  id: string
  name: string
  line: number
  shift: number
  enabled: boolean
  days: number[]
  locked: boolean[]
  installed_capacity: number[]
  output: number[]
  crew_pct: number[]
  efficiency: number[]
  direct: number[]
  indirect: number[]
  pieces: number[]
}

export interface Kpis {
  weeks_below_min: number
  weeks_above_max: number
  weeks_in_band: number
  weeks_horizon_edge: number
  first_shortage: string | null
  average_doh: number
  peak_inventory_value: number
  overtime_days: number
  days_not_worked: number
  production_hours: number
  peak_headcount: number
  weeks_over_mpw: number
}

export interface Grid {
  value_stream: {
    id: string
    name: string
    project: string
    plant: string
    min_doh: number
    max_doh: number
    mpw: number
    mode: string
    opening_stock: number
  }
  buckets: Bucket[]
  monitoring: {
    open_orders: number[]
    demonstrated_capacity: number[]
    net_balance: number[]
    cumulative_balance: number[]
    doh: number[]
    status: Status[]
    daily_consumption: number[]
    inventory: number[]
    demand: number[]
  }
  crews: CrewRow[]
  totals: {
    standard_shifts: number[]
    production: number[]
    daily_production: number[]
    overtime_days: number[]
    days_not_worked: number[]
    over_mpw: boolean[]
    activity_hours: number[]
  }
  kpis: Kpis
  can_revert: boolean
  restored_cells?: number
  recompute?: { elapsed_ms: number; budget_ms: number; within_budget: boolean }
}

/* The grid alone, without the plan screen's extras - what a simulation carries. */
export type GridBody = Pick<Grid, 'value_stream' | 'buckets' | 'monitoring' | 'crews' | 'totals' | 'kpis'>

export interface StreamSummary {
  id: string
  name: string
  project: string
  min_doh: number
  max_doh: number
  mpw: number
  crews: number
  weeks_below_min: number
  weeks_in_band: number
  weeks_above_max: number
  weeks_horizon_edge: number
  average_doh: number
  first_shortage: string | null
  overtime_days: number
  in_band: boolean
  /* The simulation the family is shown with; null = its plan. Every figure
     of the row is already that simulation's. */
  shown_simulation: { id: number; name: string; mode: string } | null
}

/* What still misses the corridor after a simulation, and why. */
export interface Explanation {
  feasible: boolean
  changed_cells: number
  headline: string
  remedy: string | null
  affected_buckets: number
  issues: {
    week: string
    bucket: number
    bucket_count: number
    first_bucket: number
    kind: string
    doh: number
    gap_days: number
    binding: string | null
    production: number
    inherited: boolean
  }[]
}

/* A simulation - automatic or manual, the same object. */

export type SimulationMode = 'auto' | 'manual'
export type ManualRule = 'keep' | 'plant_calendar' | 'fixed' | 'stop'

export interface SimulationOptions {
  allow_overtime?: boolean
  overtime_max_days?: number
  frozen_buckets?: number | null
  rule?: ManualRule
  days?: number
  from_bucket?: number | null
  to_bucket?: number | null
  /* crew codes; absent = every crew */
  crews?: string[] | null
}

export interface CrewOverride {
  installed_capacity?: number
  crew_pct?: number
  efficiency?: number
  direct_people?: number
  indirect_people?: number
  enabled?: boolean
}

export interface SimulationOverrides {
  min_doh?: number
  max_doh?: number
  mpw?: number
  wsd_minutes?: number
  standard_price?: number
  crews?: Record<string, CrewOverride>
}

export interface SimulationKpis {
  weeks_in_band: number
  weeks_below_min: number
  weeks_above_max: number
  overtime_days: number
  average_doh: number
}

export interface SimulationSummary {
  id: number
  batch: string
  name: string
  mode: SimulationMode
  value_stream: string
  options: SimulationOptions
  has_overrides: boolean
  changed_cells: number
  feasible: boolean
  headline: string
  elapsed_ms: number
  before: SimulationKpis
  after: SimulationKpis
  created_at: string
  created_by: string
  updated_at: string | null
  updated_by: string | null
  applied_at: string | null
  applied_by: string | null
}

interface MasterData {
  min_doh: number
  max_doh: number
  mpw: number
  wsd_minutes: number
  standard_price: number
}

interface SimulatedSeries {
  doh: number[]
  status: Status[]
  inventory: number[]
  production: number[]
}

export interface SimulationDetail extends SimulationSummary {
  overrides: SimulationOverrides
  explanation: Explanation
  kpis: { before: Kpis; after: Kpis }
  master_data: { stored: MasterData; simulated: MasterData }
  changes: { crew_index: number; bucket_index: number; before: number; after: number }[]
  grid: GridBody
  buckets: { index: number; type: 'D' | 'W'; label: string; date: string; plant_days: number; max_days: number }[]
  series: { demand: number[]; before: SimulatedSeries; after: SimulatedSeries }
  crews: {
    index: number
    code: string
    name: string
    installed_capacity: number
    crew_pct: number
    efficiency: number
    direct_people: number
    indirect_people: number
    enabled: boolean
    days_before: number[]
    days_after: number[]
    locked: boolean[]
  }[]
}

export interface SimulationRequest {
  mode: SimulationMode
  name: string
  options: SimulationOptions
  overrides: SimulationOverrides
}

export interface NewSimulationRequest extends SimulationRequest {
  value_streams: string[]
  /* Each family's own master data for this simulation. */
  overrides_by_family: Record<string, SimulationOverrides>
}

export interface Dashboard {
  cycle: string
  state: string
  scenario: string
  value_streams: string[]
  panels: {
    /* edge: the horizon ends inside the week's forward cover - not judged. */
    doh: Record<string, { label: string; value: number; min_doh: number; max_doh: number; status: Status; edge: boolean }[]>
    demand_vs_capacity: {
      demand: { label: string; value: number }[]
      production: { label: string; value: number }[]
      over_mpw: string[]
    }
    inventory_value: { label: string; value: number }[]
    activity_hours: { label: string; value: number }[]
    headcount: { label: string; value: number }[]
    work_plan: {
      overtime: { label: string; value: number }[]
      days_not_worked: { label: string; value: number }[]
    }
  }
  availability: Record<string, { available: boolean; blocked_on: string }>
  alerts: {
    severity: 'critical' | 'warning' | 'info'
    value_stream: string
    text: string
    go: string
    target: string
  }[]
  summary: {
    value_streams: number
    in_band: number
    with_shortage: number
    with_excess: number
    overtime_days: number
    production_hours: number
    peak_inventory_value: number
  }
}

export interface CellExplanation {
  bucket: { index: number; label: string; date: string }
  doh: {
    value: number
    rule: string
    status: Status
    min_doh: number
    max_doh: number
    horizon_edge: boolean
  }
  inventory: { value: number; rule: string; previous: number; production: number; demand: number }
  crews: {
    crew: string
    days: number
    installed_capacity: number
    crew_pct: number
    efficiency: number
    output: number
    pieces: number
    rule: string
  }[]
  demonstrated_capacity: {
    value: number
    rule: string
    open_orders: number
    net_balance: number
  }
}


/* The parameters screen: the manual's Home settings and its Master Data. */

export interface CrewParams {
  id: number
  code: string
  line: number
  shift: number
  installed_capacity: number
  crew_pct: number
  efficiency: number
  direct_people: number
  indirect_people: number
  enabled: boolean
}

export interface ValueStreamParams {
  id: number
  name: string
  project: string
  min_doh: number
  max_doh: number
  mpw: number
  wsd_minutes: number
  standard_price: number
  crews: CrewParams[]
}

export interface Parameters {
  plant: {
    code: string
    name: string
    currency: string
    settings: {
      working_hours_per_shift: number
      direct_cost_per_hour: number
      indirect_cost_per_hour: number
      open_orders_mode: string
      calc_mode: string
      daily_bucket_weeks: number
      frozen_buckets: number
    }
    choices: {
      open_orders_mode: { id: string; label: string; note: string }[]
      calc_mode: { id: string; label: string; note: string }[]
    }
  }
  value_streams: ValueStreamParams[]
  note: string
}

import { authedFetch } from './auth'

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  // Through authedFetch, so every request carries the access token and a
  // single 401 triggers one refresh rather than bouncing the user to the
  // sign-in screen every fifteen minutes.
  const response = await authedFetch(`/api${path}`, init)
  if (!response.ok) {
    // The API refuses invalid edits with a message meant for the planner
    // (SIM-08), so surface it rather than a status code.
    let detail = `${response.status} ${response.statusText}`
    try {
      const body = await response.json()
      if (body.detail) detail = body.detail
    } catch {
      /* no JSON body */
    }
    throw new Error(detail)
  }
  return response.json() as Promise<T>
}

export const api = {
  health: () => call<Record<string, unknown>>('/health'),

  cycle: () => call<{ id: string; state: string; scenarios: { id: string; name: string; read_only: boolean }[] }>('/cycle'),

  valueStreams: (scenario = 'Working') =>
    call<{ cycle: string; state: string; value_streams: StreamSummary[] }>(
      `/value-streams?scenario=${encodeURIComponent(scenario)}`,
    ),

  grid: (id: string, scenario = 'Working') =>
    call<Grid>(`/value-streams/${encodeURIComponent(id)}/grid?scenario=${encodeURIComponent(scenario)}`),

  edit: (id: string, edits: { crew_index: number; bucket_index: number; days: number }[], scenario = 'Working') =>
    call<Grid>(`/value-streams/${encodeURIComponent(id)}/edit`, {
      method: 'POST',
      body: JSON.stringify({ scenario, edits }),
    }),

  lock: (id: string, edits: { crew_index: number; bucket_index: number; days: number }[], scenario = 'Working') =>
    call<Grid>(`/value-streams/${encodeURIComponent(id)}/lock`, {
      method: 'POST',
      body: JSON.stringify({ scenario, edits }),
    }),

  revertPlan: (id: string, scenario = 'Working') =>
    call<Grid>(`/value-streams/${encodeURIComponent(id)}/revert`, {
      method: 'POST',
      body: JSON.stringify({ scenario }),
    }),

  explainCell: (id: string, bucket: number, scenario = 'Working') =>
    call<CellExplanation>(
      `/value-streams/${encodeURIComponent(id)}/explain/${bucket}?scenario=${encodeURIComponent(scenario)}`,
    ),

  /* No family = all of them. */
  dashboard: (scenario = 'Working', valueStreams: string[] = []) =>
    call<Dashboard>(
      `/dashboard?scenario=${encodeURIComponent(scenario)}` +
        valueStreams.map((v) => `&value_stream=${encodeURIComponent(v)}`).join(''),
    ),

  reports: (scenario = 'Working') =>
    call<{ reports: { id: string; name: string; coefficient: string; goes_to: string; available: boolean; provisional?: boolean; blocked_on?: string }[]; note: string }>(
      `/reports?scenario=${encodeURIComponent(scenario)}`,
    ),

  /* RPT-05: one report's figures, for the on-screen view. */
  reportTable: (reportId: string, scenario = 'Working') =>
    call<{
      report: Record<string, unknown>
      rows: { value_stream: string; project: string; weeks: string[]; values: number[] }[]
    }>(`/reports/table/${encodeURIComponent(reportId)}?scenario=${encodeURIComponent(scenario)}`),

  parameters: () => call<Parameters>('/parameters'),

  writeSettings: (body: Parameters['plant']['settings']) =>
    call<{ changed: string[] }>('/parameters/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  writeValueStream: (
    id: number,
    body: { min_doh: number; max_doh: number; mpw: number; wsd_minutes: number; standard_price: number },
  ) =>
    call<{ changed: string[] }>(`/parameters/value-streams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  writeCrew: (id: number, body: Omit<CrewParams, 'id' | 'code' | 'line' | 'shift'>) =>
    call<{ changed: string[] }>(`/parameters/crews/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  audit: () =>
    call<{ count: number; entries: { at: string; actor: string; action: string; target: string; old: unknown; new: unknown }[] }>(
      '/audit',
    ),

  /* Download a report.
   *
   * NOT an <a href>: a plain anchor is a browser navigation, and a navigation
   * does not carry the Authorization header - the access token lives in
   * memory and is attached by authedFetch. Under AUTH_MODE=local an anchor
   * therefore gets a 401 and the user sees nothing happen.
   *
   * So the bytes are fetched as an authenticated request and handed to the
   * browser as a blob, which also lets a failure surface as a message instead
   * of a blank tab.
   */
  downloadReport: (reportId: string, scenario = 'Working') =>
    downloadFile(
      `/api/reports/${encodeURIComponent(reportId)}.xlsx?scenario=${encodeURIComponent(scenario)}`,
      `${reportId}.xlsx`,
    ),

  simulations: (valueStream?: string) =>
    call<{ simulations: SimulationSummary[]; chosen: Record<string, number | null> }>(
      '/simulations' + (valueStream ? `?value_stream=${encodeURIComponent(valueStream)}` : ''),
    ),

  createSimulations: (body: NewSimulationRequest) =>
    call<{ batch: string; simulations: SimulationSummary[] }>('/simulations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  simulation: (id: number) => call<SimulationDetail>(`/simulations/${id}`),

  /* Which simulation a family is shown with on the dashboard; null = the plan. */
  chooseSimulation: (valueStream: string, simulationId: number | null) =>
    call<{ value_stream: string; simulation_id: number | null }>(
      `/simulations/choice/${encodeURIComponent(valueStream)}`,
      { method: 'PUT', body: JSON.stringify({ simulation_id: simulationId }) },
    ),

  updateSimulation: (id: number, body: SimulationRequest) =>
    call<SimulationSummary>(`/simulations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteSimulation: (id: number) =>
    call<{ deleted: number }>(`/simulations/${id}`, { method: 'DELETE' }),

  /* Write the simulation's working days into the plan the reports read. */
  applySimulation: (id: number) =>
    call<SimulationSummary & { applied_cells: number }>(`/simulations/${id}/apply`, {
      method: 'POST',
    }),

  /* The simulation as a workbook: summary before/after, the grid, every
     changed cell, and its provenance. */
  downloadSimulation: (id: number) =>
    downloadFile(`/api/simulations/${id}/simulation.xlsx`, `Simulation_${id}.xlsx`),
}

async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const response = await authedFetch(path)
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`
    try {
      detail = (await response.json()).detail ?? detail
    } catch {
      /* not a JSON error body */
    }
    throw new Error(detail)
  }

  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^"]+)"?/.exec(disposition)
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = match?.[1] ?? fallbackName
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoke on the next tick: Safari aborts the download if the object URL
  // disappears in the same frame as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
